import api from "../../services/apis/antecipeApi";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { parseBrt } from "./expired";

/** Janela de reabertura do expirado (§7.2): `expired_at > agora − 30 d`. */
export const REOPEN_WINDOW_DAYS = 30;
const DAY_MS = 86_400_000;

type Raw = Record<string, unknown>;

/** `POST antecipebackoffice/advancepayment/:id/reopen` com `{text?}` (§8.2, operator). Devolve o pacote. */
export async function reopenPackage(
  id: number,
  text?: string,
): Promise<unknown> {
  const trimmed = text?.trim();
  const response = await api.post(
    `antecipebackoffice/advancepayment/${id}/reopen`,
    trimmed ? { text: trimmed } : {},
  );
  return response.data?.data ?? response.data;
}

type ExpiredRow = {
  status?: string;
  expired_at?: string | null;
  /** BE-1h (só na lista): origem do →expired; `legacy_cleanup` não reabre (409 REOPEN_LEGACY). */
  expired_source?: string | null;
};

/** Expirado pela limpeza do legado (BE-1h)? Campo ausente = não (segue a regra dos 30 d). */
export function isLegacyExpired(row: ExpiredRow): boolean {
  if (row?.status !== "expired") return false;
  return row.expired_source === "legacy_cleanup";
}

/** Pode reabrir? Sem `expired_at` (BE antigo), deixa o BE decidir. */
export function canReopen(
  row: ExpiredRow,
  now: Date = new Date(),
): { allowed: boolean; hint: string | null } {
  if (row?.status !== "expired") return { allowed: false, hint: null };
  if (isLegacyExpired(row))
    return { allowed: false, hint: "Expirado na limpeza do legado" };
  if (!row.expired_at) return { allowed: true, hint: null };
  const at = parseBrt(row.expired_at);
  if (Number.isNaN(at)) return { allowed: true, hint: null };
  return now.getTime() - at < REOPEN_WINDOW_DAYS * DAY_MS
    ? { allowed: true, hint: null }
    : { allowed: false, hint: `Prazo de ${REOPEN_WINDOW_DAYS} dias encerrado` };
}

const MESSAGES: Record<string, string> = {
  REOPEN_WINDOW_CLOSED: `Passaram ${REOPEN_WINDOW_DAYS} dias desde que o pacote expirou; ele não pode mais ser reaberto.`,
  REOPEN_NOT_EXPIRED: "Este pacote não está mais expirado. Atualize a lista.",
  REOPEN_LEGACY: "Pacote encerrado na limpeza do legado não pode ser reaberto.",
};

/** Mensagem dos 409 da reabertura; no de duplicidade, o id do pacote vivo (`extra.advancePaymentId`). */
export function reopenErrorOf(error: unknown): {
  message: string;
  livePackageId: number | null;
} {
  const code = getApiErrorCode(error);
  if (code === "REOPEN_VACANCY_IN_LIVE_PACKAGE") {
    const data = ((error as { response?: { data?: Raw } })?.response?.data ??
      {}) as Raw;
    const extra = (
      data.extra && typeof data.extra === "object" ? data.extra : {}
    ) as Raw;
    const id = Number(data.advancePaymentId ?? extra.advancePaymentId);
    const live = Number.isInteger(id) && id > 0 ? id : null;
    return {
      message: `Um dos plantões já está em outro pedido em andamento${live ? ` (#${live})` : ""}. Reabrir criaria duplicidade.`,
      livePackageId: live,
    };
  }
  if (code && MESSAGES[code])
    return { message: MESSAGES[code], livePackageId: null };
  return {
    message: getApiErrorMessage(
      error,
      "Não foi possível reabrir o pacote. Tente de novo.",
    ),
    livePackageId: null,
  };
}
